import { useVerticalFlowFormLogic } from "@/components/ClustersPage/VerticalFlowComponents/hooks/useVerticalFlowFormLogic";
import { PERSISTED_CONTAINER_CLUSTER_KEY } from "@/utils/cluster";
import { containerVerticalFlowAnalytics } from "../analytics";
import { DynamicSupplyHardware, DynamicSupplyHardwareFiltersResponse } from "@/types";

export type ContainerFormStep = "recipes" | "processor" | "configure" | "deployment";

export const useContainerFormLogic = (
  formData: unknown,
  isLg: boolean,
  recipesFirstEnabled: boolean,
  recipesFirstFlagResolved: boolean,
  fetchHardwareWithFilters: (options?: {
    abortController?: AbortController;
    search?: string;
    filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
  }) => Promise<{
    hardware: DynamicSupplyHardware[];
    filters: DynamicSupplyHardwareFiltersResponse;
  }>
) => {
  const formLogic = useVerticalFlowFormLogic<ContainerFormStep>(formData, isLg, {
    analytics: containerVerticalFlowAnalytics,
    persistedStorageKey: PERSISTED_CONTAINER_CLUSTER_KEY,
    processorStepTitle: "Select Your Container Processor",
    defaultStepState: {
      recipes: recipesFirstEnabled ? "current" : "next",
      processor: recipesFirstEnabled ? "next" : "current",
      configure: "next",
      deployment: "next"
    },
    fetchHardwareWithFilters,
    onRetrievePersistedState: (persistedState) => {
      const hasPersistedRecipe = Boolean(persistedState?.containerRecipe);
      if (recipesFirstEnabled && !hasPersistedRecipe) {
        return {
          recipes: "current",
          processor: "next",
          configure: "next",
          deployment: "next"
        };
      }
      return {
        recipes: "completed",
        processor: "completed",
        configure: "completed",
        deployment: "current"
      };
    },
    isSubmitPossible: (currentStep, values, errors) => {
      if (currentStep === "configure") {
        const hasContainerImage = Boolean(values.containerImage?.trim());
        if (!hasContainerImage) return false;

        if (values.isPrivate) {
          const hasCredentials =
            Boolean(values.username?.trim()) && Boolean(values.secret?.trim());
          if (!hasCredentials) return false;
        }

        if (errors.containerImage) return false;
        if (errors.command) return false;
        if (errors.trafficPort) return false;
        const trafficPort = values.trafficPort;
        if (
          trafficPort === undefined ||
          trafficPort === null ||
          String(trafficPort).trim() === ""
        ) {
          return false;
        }
        if (values.isPrivate && errors.username) return false;
        if (values.isPrivate && errors.secret) return false;
        return true;
      }
      return true;
    },
    isIntermediateStep: (currentStep) => currentStep === "configure",
    getNextStep: (currentStep) => {
      if (currentStep === "recipes") return "processor";
      if (currentStep === "processor") return "configure";
      if (currentStep === "configure") return "deployment";
      return currentStep;
    },
    getPreviousStep: (currentStep) => {
      if (currentStep === "processor") return recipesFirstEnabled ? "recipes" : "processor";
      if (currentStep === "configure") return "processor";
      if (currentStep === "deployment") return "configure";
      return currentStep;
    },
    advanceStepOnFeaturedHardwareDeepLink: !recipesFirstEnabled,
    featuredHardwareDeepLinkConfigReady: recipesFirstFlagResolved
  });

  return {
    ...formLogic,
    onNextRecipesStep: () => formLogic.onCompleteStep("recipes")
  };
};
